import { useRef, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { USER_TYPE_CLIENT } from 'types/auth/types';
import axiosService from 'utils/axios';
import InfiniteScroll from 'react-infinite-scroll-component';
import LoadingSpinner from 'components/Shared/LoadingSpinner/LoadingSpinner';
import { vehicleListItemStyles } from './styles';
import VehicleListItem from './VehicleListItem';
import { Vehicle } from 'types/vehicles/types';
import { Box, Typography } from '@mui/material';
import { PaginationInfo } from 'types/general/types';
import { debounce } from 'lodash';

const FETCH_VEHICLES_COUNT = 10;
const SEARCH_DEBOUNCE_TIME_MS = 500;

type Props = {
  filterByCurrentUser?: boolean;
  clientId?: string;
  searchText?: string;
  shortRepresentation?: boolean;
  scrollableTarget?: string;
  refreshKey?: number;
};

export default function PaginatedVehicleList({
  filterByCurrentUser = false,
  clientId: clientIdProp,
  searchText = '',
  shortRepresentation = false,
  scrollableTarget = 'dashboardContentContainer',
  refreshKey = 0,
}: Props) {
  const user = useSelector((state: RootState) => state.auth.user);
  const clientId = clientIdProp ?? (filterByCurrentUser || user?.user_type === USER_TYPE_CLIENT ? user?.id ?? '' : '');
  const [isLoading, setIsLoading] = useState(true);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const vehiclePaginationInfoRef = useRef<PaginationInfo>({
    count: 0,
    next: `/vehicles/vehicle/?limit=${FETCH_VEHICLES_COUNT}&offset=0&client__id=${clientId}&search=`,
  });
  const searchAbortControllerRef = useRef<AbortController | undefined>();
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  const getNextVehicles = useCallback((currentVehiclesList: Vehicle[], search: string, withLoadingIndicator = true) => {
    if (vehiclePaginationInfoRef.current.next) {
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }

      if (withLoadingIndicator) {
        setIsLoading(true);
      }
      searchAbortControllerRef.current = new AbortController();
      return axiosService
        .get(`${vehiclePaginationInfoRef.current.next}${search}`, {
          signal: searchAbortControllerRef.current.signal,
        })
        .then(res => {
          vehiclePaginationInfoRef.current = {
            count: res.data.count,
            next: res.data.next,
          };
          setVehicles([...currentVehiclesList, ...res.data.results]);
          if (withLoadingIndicator) {
            setIsLoading(false);
          }
        });
    }
    return Promise.resolve(currentVehiclesList);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((search: string) => {
      vehiclePaginationInfoRef.current = {
        count: 0,
        next: `/vehicles/vehicle/?limit=${FETCH_VEHICLES_COUNT}&offset=0&client__id=${clientId}&search=`,
      };
      getNextVehicles([], search);
    }, SEARCH_DEBOUNCE_TIME_MS),
    [clientId]
  );

  useEffect(() => {
    debouncedSearch(searchText);
  }, [debouncedSearch, searchText]);

  useEffect(() => {
    if (!refreshKey) {
      return;
    }
    vehiclePaginationInfoRef.current = {
      count: 0,
      next: `/vehicles/vehicle/?limit=${FETCH_VEHICLES_COUNT}&offset=0&client__id=${clientId}&search=`,
    };
    getNextVehicles([], searchTextRef.current);
  }, [refreshKey, clientId, getNextVehicles]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (vehicles.length === 0) {
    return (
      <Typography variant="body1" color="primary">
        No vehicles found.
      </Typography>
    );
  }

  return (
    <InfiniteScroll
      hasMore={!!vehiclePaginationInfoRef.current.next}
      dataLength={vehicles.length}
      next={() => getNextVehicles(vehicles, '', false)}
      scrollableTarget={scrollableTarget}
      loader={<Typography style={{ width: '100%', textAlign: 'center', marginTop: '20px' }}>Loading...</Typography>}
    >
      {vehicles.map((vehicle: Vehicle, index) => (
        <Box key={vehicle.id}>
          <VehicleListItem vehicle={vehicle} shortRepresentation={shortRepresentation} />
          {index < vehicles.length - 1 ? <div style={vehicleListItemStyles.divider}></div> : null}
        </Box>
      ))}
    </InfiniteScroll>
  );
}
